// ============================================
// useAuthBroadcast — Cross-tab auth coordination
// Simple BroadcastChannel for logout/login sync.
// No leader election needed (unlike notifications).
// ============================================

import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';

type AuthMessage = { type: 'logout' } | { type: 'session-expired' } | { type: 'logged-in' };

const CHANNEL_NAME = 'wp-auth';

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
  }
  return channel;
}

/** Build /login path preserving redirect URI */
function buildLoginUrl(redirectPath?: string): string {
  if (redirectPath && redirectPath !== '/' && redirectPath !== '/login') {
    return `/login?redirect_to=${encodeURIComponent(redirectPath)}`;
  }
  return '/login';
}

/** Broadcast logout and redirect to login with current path as redirect URI */
export function broadcastLogout(): void {
  getChannel()?.postMessage({ type: 'logout' } satisfies AuthMessage);
}

/** Broadcast session-expired (called from axios 401 interceptor) */
export function broadcastSessionExpired(): void {
  getChannel()?.postMessage({ type: 'session-expired' } satisfies AuthMessage);
}

/** Broadcast that a session was established (called after successful session fetch) */
export function broadcastLoggedIn(): void {
  getChannel()?.postMessage({ type: 'logged-in' } satisfies AuthMessage);
}

/**
 * Listen for auth events from other tabs.
 * - logout / session-expired: redirect to /login with redirect URI
 * - logged-in: reload so session is picked up
 */
export function useAuthBroadcast(): void {
  useEffect(() => {
    const ch = getChannel();
    if (!ch) return;

    const handler = (event: MessageEvent<AuthMessage>) => {
      const { type } = event.data;

      if (type === 'logout' || type === 'session-expired') {
        // Redirect to login — preserve this tab's current path as redirect
        const redirectPath = window.location.pathname + window.location.search;
        window.location.href = buildLoginUrl(redirectPath);
      }

      if (type === 'logged-in') {
        // Another tab logged in — reload only if this tab has no session yet
        // (e.g., sitting on /login). Prevents infinite reload loop when
        // duplicating tabs, since both tabs would otherwise broadcast
        // logged-in back and forth.
        const hasSession = !!useAuthStore.getState().session;
        if (!hasSession) {
          window.location.reload();
        }
      }
    };

    ch.addEventListener('message', handler);
    return () => ch.removeEventListener('message', handler);
  }, []);
}
